import React, { useState, useEffect } from 'react';
import { getPerformanceKpi, getClassement, getEvolution } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Chart from 'react-apexcharts';
import { FiBarChart2, FiTrendingUp, FiAward } from 'react-icons/fi';

const Performance: React.FC = () => {
    const { user } = useAuth();
    const [kpi, setKpi] = useState<any>(null);
    const [classement, setClassement] = useState<any[]>([]);
    const [evolution, setEvolution] = useState<any[]>([]);
    const [periode, setPeriode] = useState('mois');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            getPerformanceKpi(),
            getClassement({ periode }),
            getEvolution()
        ]).then(([k, c, e]) => {
            setKpi(k.data); setClassement(c.data); setEvolution(e.data);
        }).finally(() => setLoading(false));
    }, [periode]);

    if (loading) return <div className="loading">Chargement...</div>;

    const evoOpts: ApexCharts.ApexOptions = {
        chart: { type: 'area', toolbar: { show: true }, fontFamily: 'DM Sans, sans-serif', zoom: { enabled: true } },
        stroke: { width: [3, 3], curve: 'smooth' },
        colors: ['#16A34A', '#3B82F6'],
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 } },
        xaxis: {
            categories: evolution.map((e: any) => e.date),
            labels: { formatter: (d: string) => new Date(d).toLocaleDateString('fr', { day: '2-digit', month: 'short' }), rotateAlways: false }
        },
        yaxis: [
            { title: { text: 'Activites' }, labels: { formatter: (v: number) => Math.round(v).toString() } },
            { opposite: true, title: { text: 'CA (F)' }, labels: { formatter: (v: number) => v.toLocaleString() } }
        ],
        legend: { position: 'top' },
        tooltip: { shared: true, intersect: false, x: { formatter: (v: number) => evolution[v - 1]?.date ? new Date(evolution[v - 1].date).toLocaleDateString('fr') : '' } },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
    };

    const evoSeries = [
        { name: 'Activites', data: evolution.map((e: any) => e.nb_activites || 0) },
        { name: 'CA (F)', data: evolution.map((e: any) => e.ca || 0) }
    ];

    return (
        <div className="page-performance">
            <div className="page-header"><h2><FiBarChart2 /> Suivi des Performances</h2></div>

            {kpi && (
                <div className="kpi-grid">
                    <div className="kpi-card" style={{ borderLeftColor: '#16A34A' }}>
                        <div className="kpi-content"><span className="kpi-value">{kpi.total}</span><span className="kpi-label">Activites</span></div>
                    </div>
                    <div className="kpi-card" style={{ borderLeftColor: '#22C55E' }}>
                        <div className="kpi-content"><span className="kpi-value">{(kpi.ca || 0).toLocaleString()} F</span><span className="kpi-label">Chiffre d'affaires</span></div>
                    </div>
                    <div className="kpi-card" style={{ borderLeftColor: '#F59E0B' }}>
                        <div className="kpi-content"><span className="kpi-value">{kpi.clients_visites}</span><span className="kpi-label">Clients visites</span></div>
                    </div>
                    <div className="kpi-card" style={{ borderLeftColor: '#8B5CF6' }}>
                        <div className="kpi-content"><span className="kpi-value">{kpi.taux_conversion}%</span><span className="kpi-label">Taux conversion</span></div>
                    </div>
                    <div className="kpi-card" style={{ borderLeftColor: '#EF4444' }}>
                        <div className="kpi-content"><span className="kpi-value">{kpi.jours_actifs}</span><span className="kpi-label">Jours actifs</span></div>
                    </div>
                    <div className="kpi-card" style={{ borderLeftColor: '#F97316' }}>
                        <div className="kpi-content"><span className="kpi-value">{Math.round(kpi.objectifs?.taux_moyen || 0)}%</span><span className="kpi-label">Objectifs atteints</span></div>
                    </div>
                </div>
            )}

            <div className="charts-grid">
                <div className="chart-card">
                    <div className="chart-header">
                        <h3><FiTrendingUp /> Evolution (30 jours)</h3>
                    </div>
                    <Chart options={evoOpts} series={evoSeries} type="area" height={300} />
                </div>

                <div className="chart-card">
                    <div className="chart-header">
                        <h3><FiAward /> Classement</h3>
                        <select value={periode} onChange={e => setPeriode(e.target.value)}>
                            <option value="jour">Jour</option><option value="semaine">Semaine</option><option value="mois">Mois</option><option value="annee">Annee</option>
                        </select>
                    </div>
                    <div className="classement-list">
                        {classement.map((c, i) => (
                            <div key={c.id} className={`classement-item ${c.id === user?.id ? 'highlight' : ''}`}>
                                <span className={`rang ${i < 3 ? 'top' : ''}`}>{c.rang}</span>
                                <span className="nom">{c.nom}</span>
                                <span className="stat">{(c.ca || 0).toLocaleString()} F</span>
                                <span className="stat">{c.nb_activites} act.</span>
                                <span className="stat">{c.clients} clients</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Performance;
